import { forwardRef, lazy, Suspense, useSyncExternalStore, type Ref } from 'react'
import type { Components, Options, UrlTransform } from 'react-markdown'
import type { AnyExtension, Editor } from '@tiptap/core'

import type { MarkdownFeatures } from './features'
import type { MentionablesHandler } from './mentionables/types'
import { CodeBlock } from './nested/code-block'
import { Paragraph } from './nested/paragraph'
import { Prose } from './nested/prose'
import { ReadOnly } from './nested/read-only'
import { Table } from './nested/table'
import { remarkKbd, remarkMark } from './plugins'
import type { MarkdownImageRenderer, MarkdownLadder, MarkdownSize } from './types'

// Loaded by `lazy` only when an editable `Markdown` renders, so Tiptap stays out of every read-only bundle.
const Editable = lazy(() => import('./nested/editable'))

interface MarkdownProps {
  /** Markdown source. Read-only unless `editable`. */
  children?: string
  /**
   * Source for an `editable` Markdown. Kept in sync as it changes, except while the editor has
   * focus, so a late update never overwrites what the user is typing. `children` is used if this is
   * omitted.
   */
  value?: string
  /** Seeds an `editable` Markdown once, on mount. Later changes are ignored, so the editor owns its text. */
  defaultValue?: string
  /**
   * Called with the markdown source as the document changes, and the Tiptap editor behind it. Fires
   * for programmatic changes too (the editor may normalise its input), so check `editor.isFocused`
   * to tell the user's edits apart. `editable` only.
   */
  onChange?: (markdown: string, editor: Editor) => void
  /** Shown while an `editable` document is empty. */
  placeholder?: string
  /** Renders a Tiptap editor that looks like the read-only output. Loaded on demand. */
  editable?: boolean
  /** Locks an `editable` Markdown without unmounting the editor, so its content and history survive. */
  readOnly?: boolean
  /** Focus an `editable` Markdown on mount. A number is a document position. */
  autoFocus?: boolean | 'start' | 'end' | 'all' | number
  /**
   * Which formatting an `editable` Markdown allows: omit for everything, or pass a preset
   * (`slackFeatures`, `githubFeatures`, ...) and override fields.
   */
  features?: MarkdownFeatures
  /** Platform-aware `@`/`#` mentions for an `editable` Markdown, e.g. `createSlackMentionables`. */
  mentionables?: MentionablesHandler
  /** Hard cap on an `editable` document, in plain-text characters. */
  maxLength?: number
  /** Extra Tiptap extensions for an `editable` Markdown, appended after the built-in set. Read once, on mount. */
  extensions?: Array<AnyExtension>
  /**
   * The formatting bar of an `editable` Markdown: `bubble` floats over a selection, `docked` is
   * always visible above the text, `false` has none. Defaults to `bubble`.
   */
  toolbar?: 'bubble' | 'docked' | false
  /** Goes on the docked toolbar, e.g. to make it sticky inside a scrolling page. */
  toolbarClassName?: string
  /**
   * Receives the Tiptap editor once it has loaded, and `null` when it unmounts. Pass a state setter
   * or a ref object, not an inline function, or it is called again on every render.
   */
  editorRef?: Ref<Editor>
  /**
   * `md` is body copy; `sm` is the dense reading size, with tighter rhythm. `inherit` sets no
   * type or colour of its own, so the text takes the font and colour of whatever it sits in, with
   * `md` rhythm.
   */
  size?: MarkdownSize
  /**
   * Which type scale the headings use. `document` is for a page: `##` is the section heading and
   * everything below steps down from it. `compact` is the same idea for a narrow column such as a side panel:
   * `#` at 22px, `##` at 18px, the rest at body size or below. `specimen` runs the full scale, `#` at display size.
   */
  ladder?: MarkdownLadder
  /** Caps paragraphs at 72ch. Off by default: most surfaces already set their own width. */
  measure?: boolean
  /** A line-number gutter on code blocks. */
  lineNumbers?: boolean
  /** Component overrides, merged over the defaults. */
  components?: Components
  /** Appended after remark-gfm. See `Markdown.remarkMark` and `Markdown.remarkKbd`. */
  remarkPlugins?: NonNullable<Options['remarkPlugins']>
  /** Appended after the built-in table and task-list transforms. */
  rehypePlugins?: NonNullable<Options['rehypePlugins']>
  /** Replaces react-markdown's URL sanitizer, which blanks unknown protocols. */
  urlTransform?: UrlTransform
  /** Renders image nodes, for `src` values that are app pseudo-URLs. Read-only and editable. */
  renderImage?: MarkdownImageRenderer
  /** Goes on the `.markdown` element. */
  className?: string
  /**
   * Read-only only: renders just the parsed blocks, with no `.markdown` root, so `size`, `ladder`,
   * `measure`, `className` and `ref` have nothing to attach to. Put several in one `Markdown.Prose`
   * to render a long document as separately memoizable chunks; their blocks are siblings of that
   * one root, so spacing matches a single render.
   */
  bare?: boolean
}

const subscribe = () => () => {}
const useMounted = () =>
  useSyncExternalStore(
    subscribe,
    () => true,
    () => false,
  )

/**
 * The one markdown surface: Prism's markdown foundation, read-only or editable. Everything that is
 * styled by element (headings, lists, quotes, links, inline code, tables) is styled in
 * `markdown-content.css` under `.markdown`, which is why the read-only and the editable output are
 * the same by construction. The parts below are only what needs structure of its own.
 */
const MarkdownRoot = forwardRef<HTMLDivElement, MarkdownProps>(function Markdown(
  { children, value, defaultValue, editable = false, bare = false, ...props },
  ref,
) {
  const mounted = useMounted()

  const readOnlyView = <ReadOnly ref={ref} source={children ?? value ?? defaultValue ?? ''} bare={bare} {...props} />

  // Tiptap renders in the browser only, so the server and the first client render both show the
  // read-only output; it is also the Suspense fallback, so the swap to the editor does not jump.
  if (!editable || !mounted) return readOnlyView

  return (
    <Suspense fallback={readOnlyView}>
      <Editable forwardedRef={ref} value={children ?? value} defaultValue={defaultValue} {...props} />
    </Suspense>
  )
})

MarkdownRoot.displayName = 'Markdown'

const Markdown = Object.assign(MarkdownRoot, {
  Prose,
  CodeBlock,
  Table,
  Paragraph,
  remarkMark,
  remarkKbd,
})

export { Markdown }
export type { Editor, MarkdownProps }
export type { ProseProps } from './nested/prose'
export type { MarkdownImageRenderProps, MarkdownImageRenderer, MarkdownLadder, MarkdownSize } from './types'
export { extractText, isElement } from './hast'
export type { HastElement, HastNodes, HastRoot } from './hast'
export {
  slackFeatures,
  githubFeatures,
  linearFeatures,
  notionFeatures,
  gmailFeatures,
  inlineOnlyFeatures,
} from './features'
export type { HeadingLevel, ImageFeatureConfig, MarkdownFeatures, MentionTrigger } from './features'
export type { MentionSuggestionItem } from './nested/tiptap/mention-list'
export {
  createSlackMentionables,
  createNotionMentionables,
  createLinearMentionables,
  createGithubMentionables,
} from './mentionables'
export type {
  CreateSlackMentionablesOptions,
  CreateNotionMentionablesOptions,
  CreateLinearMentionablesOptions,
  CreateGithubMentionablesOptions,
} from './mentionables'
export type { MentionAttrs, MentionablesHandler, MentionablesTrigger } from './mentionables/types'
